import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { PropertyBadgeItem, PropertyCardBadgesProps } from './propertyCardBadgeTypes';
import { buildPropertyBadgesList } from './propertyCardBadgeBuilder';

export type { PropertyBadgeItem, PropertyCardBadgesProps };

export const PropertyCardBadges: React.FC<PropertyCardBadgesProps> = ({
  hasDaviviendaCreditPromo,
  tiene_oferta,
  valor_oferta,
  es_preventa,
  hasCommissionOverride,
  isNewlyAdded,
  isClientRole,
  tags,
  tag,
}) => {
  const badges = useMemo(() => {
    return buildPropertyBadgesList({
      hasDaviviendaCreditPromo,
      tiene_oferta,
      valor_oferta,
      es_preventa,
      hasCommissionOverride,
      isNewlyAdded,
      isClientRole,
      tags,
      tag,
    });
  }, [
    hasDaviviendaCreditPromo,
    tiene_oferta,
    valor_oferta,
    es_preventa,
    hasCommissionOverride,
    isNewlyAdded,
    isClientRole,
    tags,
    tag,
  ]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (badges.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % badges.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [badges.length]);

  if (badges.length === 0) return null;

  const activeBadge = badges[currentIndex % badges.length];

  return (
    <div className="absolute top-2 left-2 z-20 pointer-events-none flex items-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={activeBadge.id}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          title={activeBadge.title}
          className="pointer-events-auto flex items-center"
        >
          {activeBadge.imageUrl ? (
            <div className="relative inline-flex items-center bg-transparent border-0 shadow-none p-0">
              <img
                src={activeBadge.imageUrl}
                alt={activeBadge.imageAlt || 'Crédito Davivienda 8%'}
                referrerPolicy="no-referrer"
                loading="lazy"
                decoding="async"
                className="h-[75px] max-h-[75px] w-auto max-w-[350px] object-contain select-none bg-transparent border-none shadow-none"
              />
              {badges.length > 1 && (
                <span className="absolute -bottom-1.5 right-1 flex gap-0.5 bg-black/75 px-1 py-0.5 rounded-full">
                  {badges.map((b, idx) => (
                    <span
                      key={b.id}
                      className={`w-1 h-1 rounded-full transition-colors duration-200 ${
                        idx === currentIndex % badges.length ? 'bg-white' : 'bg-white/40'
                      }`}
                    />
                  ))}
                </span>
              )}
            </div>
          ) : (
            <div
              className={`${activeBadge.bgClass} ${activeBadge.borderClass} ${activeBadge.textClass} px-2.5 py-1 rounded-md font-black text-[9px] shadow-lg tracking-tight uppercase flex items-center gap-1.5`}
            >
              <span>{activeBadge.label}</span>
              {badges.length > 1 && (
                <span className="flex gap-0.5 ml-1 items-center">
                  {badges.map((b, idx) => (
                    <span
                      key={b.id}
                      className={`w-1 h-1 rounded-full transition-colors duration-200 ${
                        idx === currentIndex % badges.length ? 'bg-white' : 'bg-white/40'
                      }`}
                    />
                  ))}
                </span>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
